/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useRef } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { BackArrowIcon } from 'components/elements/Icons';
import { HorizontalTimeline } from 'components/elements';
import AppointmentDetailsForm from 'components/modules/booking/AppointmentDetailsForm/AppointmentDetailsForm';
import ServiceDetailsForm from 'components/modules/booking/ServiceDetailsForm/ServiceDetailsForm';
import FeesForm from 'components/modules/booking/FeesForm/FeesForm';
import { useBookingActions, useStateCallback, useFetchConfiguration, useGoogleRecaptcha, useProfile } from 'hooks';
import BookAppointmentServiceTypes from './BookAppointmentServiceTypes';
import BookAppointmentOptions from './BookAppointmentOptions';
import BookAppointmentProviders from './BookAppointmentProviders';
import BookAppointmentForm from './BookAppointmentForm';
import RequestAppointmentForm from './RequestAppointmentForm';
import BookAppointmentConfirmation from './BookAppointmentConfirmation';
import RequestAppointmentConfirmation from './RequestAppointmentConfirmation';
import Styles from './styles';

const useStyles = makeStyles(Styles);

function BookAppointment() {
  const { isEnabledSection } = useFetchConfiguration();
  const { t } = useTranslation('labels');
  const { data: brandingData } = useSelector((state) => state.branding);
  const { data: profile } = useProfile();
  const classes = useStyles();
  const { data: organizationData } = useSelector((state) => state.organization);
  const url = new URL(window.location);
  if (!url.searchParams.get('oid')) {
    url.searchParams.set('oid', organizationData.id);
    window.history.pushState(null, '', url.toString());
  }
  const [serviceDetailsFormData, setServiceDetailsFormData] = useStateCallback({});
  const [appointmentDetailsFormData, setAppointmentDetailsFormData] = useStateCallback({});
  const [feesFormData, setFeesFormData] = useStateCallback({});
  const [activeStep, setActiveStep] = useState(-1);
  const isInitialStep = activeStep === -1;
  const isLastStep = [3, 8].includes(activeStep);
  const [appointmentType, setAppointmentType] = useState('follow_up');
  const isFollowUpAppointment = appointmentType === 'follow_up';
  const selectAServiceSteps = [4];
  if (isEnabledSection('service_details')) {
    selectAServiceSteps.push(5);
  }
  if (isEnabledSection('appointment_details')) {
    selectAServiceSteps.push(6);
  }
  if (isEnabledSection('fees')) {
    selectAServiceSteps.push(7);
  }

  const [isConfirmed, setIsConfirmed] = useState(false);

  const [bookAppointmentFormData, setBookAppointmentFormData] = useState({});

  const [serviceType, setServiceType] = useState(null);
  const [provider, setProvider] = useState(null);

  const { bookOnlineIntakeMutation } = useBookingActions(true);

  const { getToken: getRecaptchaToken } = useGoogleRecaptcha();

  const submitWizzard = async () => {
    const token = await getRecaptchaToken();
    const config = {
      headers: {
        'Recaptcha-Token': token,
      },
    };

    await bookOnlineIntakeMutation.mutateAsync({
      ...serviceDetailsFormData,
      ...appointmentDetailsFormData,
      ...feesFormData,
      ...bookAppointmentFormData,
      profile,
      healthcareServiceId: serviceType.id,
      config,
    });
  };

  const steps = isFollowUpAppointment ? [0, 1, 2] : selectAServiceSteps;
  const formId = 'BookingForm';
  const submitRef = useRef();

  const getForwardStep = (step) => {
    if (step >= 8) return step;
    if (!(selectAServiceSteps + [0, 1, 2]).includes(step)) {
      return getForwardStep(step + 1);
    }
    return step;
  };

  const getBackwardStep = (step) => {
    if (step <= 0) return step;
    if (!(selectAServiceSteps + [0, 1, 2]).includes(step)) {
      return getBackwardStep(step - 1);
    }
    return step;
  };

  const selectItem = (id) => () => {
    if (id === 0) {
      setActiveStep(0);
      setAppointmentType('follow_up');
    }
    if (id === 4) {
      setActiveStep(4);
      setAppointmentType('new_service');
    }
  };

  // update check for step when implementing the other 3 request new service screens.
  const goBack = () => (activeStep === 4 ? setActiveStep(-1) : setActiveStep(getBackwardStep(activeStep - 1)));

  const goToStep = (back) => {
    if (back) {
      goBack();
    } else {
      setActiveStep(getForwardStep(activeStep + 1));
    }
  };

  const getSubmitFormFunction = (setFormData) => (data, back) => {
    setFormData(data, (formData) => {
      if (isLastStep) {
        submitWizzard(formData);
      } else {
        goToStep(back);
      }
    });
  };

  const goBackward = () => {
    if (activeStep > 4 && activeStep < 8) {
      submitRef.current.click();
    } else {
      goBack();
    }
  };

  const headerStyles = cn(classes.header, { [classes.smallHeader]: !isInitialStep });
  const subtitleStyles = cn(classes.subtitle, { [classes.activeSubtitle]: !isInitialStep });
  const bodyWrapperClasses = cn(classes.bodyWrapper, { [classes.wideBodyWrapper]: !isInitialStep && !isLastStep }, { [classes.smallBodyWrapper]: isLastStep });
  const getStepScreen = (step) => {
    const bookAppointmentSteps = {
      0: (
        <BookAppointmentServiceTypes
          onSelectServiceType={(id) => {
            setServiceType(id);
            setActiveStep(1);
          }}
          isServiceRequest={!isFollowUpAppointment}
          onClickBack={goBack}
        />
      ),
      1: (
        <BookAppointmentProviders
          healthcareServiceId={serviceType?.id}
          onSelectProvider={(id) => {
            setProvider(id);
            setActiveStep(2);
          }}
        />
      ),
      2: !provider?.showCalendarAvailability ? (
        <RequestAppointmentForm
          onSubmit={(data) => {
            setBookAppointmentFormData(data);
            setActiveStep(3);
          }}
          providerAccountId={provider?.id}
          providerName={provider?.name}
          healthcareServiceId={serviceType?.id}
          {...bookAppointmentFormData}
        />
      ) : (
        <BookAppointmentForm
          onSubmit={(data) => {
            setBookAppointmentFormData(data);
            setActiveStep(3);
          }}
          providerAccountId={provider?.id}
          providerName={provider?.name}
          healthcareServiceId={serviceType?.id}
          {...bookAppointmentFormData}
        />
      ),
      3: !provider?.showCalendarAvailability ? (
        <RequestAppointmentConfirmation
          onClickBack={goBack}
          healthcareService={serviceType}
          provider={provider}
          location={bookAppointmentFormData.location}
          availabilityPreferencesDetail={bookAppointmentFormData.availabilityPreferencesDetail}
          availabilityPreferences={bookAppointmentFormData.availabilityPreferences}
          timezone={bookAppointmentFormData.timezone}
          isConfirmed={isConfirmed}
          isServiceRequest={false}
          organisationName={organizationData.attributes.displayName}
          onConfirm={() => {
            setIsConfirmed(true);
          }}
        />
      ) : (
        <BookAppointmentConfirmation
          onClickBack={goBack}
          date={bookAppointmentFormData.date}
          healthcareService={serviceType}
          provider={provider}
          startTime={bookAppointmentFormData.slot?.start}
          endTime={bookAppointmentFormData.slot?.end}
          location={bookAppointmentFormData.location}
          timezone={bookAppointmentFormData.timezone}
          reasonForVisit={bookAppointmentFormData.reasonForVisit}
          isConfirmed={isConfirmed}
          onConfirm={() => {
            setIsConfirmed(true);
          }}
        />
      ),
      4: (
        <BookAppointmentServiceTypes
          onSelectServiceType={(id) => {
            setServiceType(id);
            setActiveStep(getForwardStep(activeStep + 1));
          }}
          isServiceRequest={!isFollowUpAppointment}
          onClickBack={goBack}
        />
      ),
      5: (
        <ServiceDetailsForm
          formId={formId}
          onSubmit={getSubmitFormFunction(setServiceDetailsFormData)}
          isServiceRequest={!isFollowUpAppointment}
          isInitialStep={isInitialStep}
          submitRef={submitRef}
          {...serviceDetailsFormData}
        />
      ),
      6: (
        <AppointmentDetailsForm
          formId={formId}
          onSubmit={getSubmitFormFunction(setAppointmentDetailsFormData)}
          isServiceRequest={!isFollowUpAppointment}
          submitRef={submitRef}
          {...appointmentDetailsFormData}
        />
      ),
      7: <FeesForm formId={formId} onSubmit={getSubmitFormFunction(setFeesFormData)} isServiceRequest={!isFollowUpAppointment} submitRef={submitRef} {...feesFormData} />,
      8: (
        <RequestAppointmentConfirmation
          onClickBack={goBack}
          healthcareService={serviceType}
          provider={provider}
          location={bookAppointmentFormData.location}
          availabilityPreferencesDetail={bookAppointmentFormData.availabilityPreferencesDetail}
          availabilityPreferences={bookAppointmentFormData.availabilityPreferences}
          timezone={bookAppointmentFormData.timezone}
          isConfirmed={isConfirmed}
          isServiceRequest={!isFollowUpAppointment}
          organisationName={organizationData.attributes.displayName}
          onConfirm={() => {
            submitWizzard();
            setIsConfirmed(true);
          }}
        />
      ),
      default: <BookAppointmentOptions onSelectOption={selectItem} />,
    };
    return bookAppointmentSteps[step] || bookAppointmentSteps.default;
  };

  const getSubtitle = (step) => {
    const subtitles = {
      0: t('select_a_service'),
      1: t('select_a_provider'),
      2: t('select_a_date_time_location'),
      3: t(!provider?.showCalendarAvailability ? 'request_an_appointment' : 'book_this_appointment'),
      default: '',
      4: t('select_a_service'),
      5: t('select_service_details'),
      6: t('select_a_date_time_location'),
      7: t('select_fee_type'),
      8: t('request_a_new_service'),
    };
    return subtitles[step] || subtitles.default;
  };

  const header = !isLastStep && (
    <Typography className={headerStyles} variant="h6" component="h6">
      {isFollowUpAppointment ? t('book_a_followup_appointment') : t('request_a_new_service')}
    </Typography>
  );

  return (
    <>
      <div className={classes.headerWrapper}>
        {!isInitialStep && !isConfirmed && (
          <button className={classes.backButton} type="button" onClick={() => goBackward()}>
            <BackArrowIcon />
          </button>
        )}
        <img className={classes.logo} src={brandingData.icon} alt="Logo Icon" />
        {isInitialStep ? (
          <Typography className={headerStyles} variant="h6" component="h6">
            {t('book_an_appointment')}
          </Typography>
        ) : (
          header
        )}
        <Typography className={subtitleStyles} variant="h5" component="h5">
          {getSubtitle(activeStep)}
        </Typography>
      </div>

      {!isLastStep && <div className={classes.progress}>{!isInitialStep && !isLastStep && <HorizontalTimeline activeStep={activeStep} steps={steps} />}</div>}

      <div className={bodyWrapperClasses}>{getStepScreen(activeStep)}</div>
    </>
  );
}

export default BookAppointment;
